import {
  defaultDepartmentId,
  isProviderOnlyDepartment,
  nonDefaultDepartmentId,
  offeredDepartmentId,
} from "../config/practice";

/*
 * The Athena department a booking from care asks for. EVERY create_schedule
 * call site goes through here (Create New Appointment, the chart's Schedule
 * tab, the orders Schedule popup, the scheduler's appointment card - create,
 * create_multiple_schedule and reschedule). Before CODE they sent the staff
 * member's or the open visit's own department, so a follow-up booked from a
 * CODE visit for a C-HIM provider would have asked Athena for dept 3.
 *
 * The rule is the API's booking_departmentid (api/utils/scope.py
 * provider_department), which stays the authority; this only stops care from
 * asking for the wrong place:
 *   1. the provider's dated move, config.department_from = {date,
 *      departmentid}, when the visit date is on or after its date (owner
 *      10-18: Dr. Adupa, every visit from 2026-11-01 is CODE);
 *   2. the provider's home department when it is a listed department other
 *      than the default (9928: 2 Frisco, 3 CODE);
 *   3. otherwise `requested` (the staff member's / the open visit's
 *      department, as before), EXCEPT a request for a provider-only location
 *      (profile.providerOnlyDepartments; 9928: CODE, 3) for a provider who is
 *      not there on that date: that books in the default department, 1. A
 *      home-2 provider never reaches this step (rule 2 gives 2).
 *
 * Where the provider's department comes from: care's providers list carries
 * only id and name, so rules 1-2 normally come from the open-slots answer.
 * daywise-open-slots returns, per slot, the department the server's rule
 * works out for that provider and date (care sends no departmentid, so a
 * provider no rule moves gets null; before the API's CODE Step 2 it is always
 * null). A slot department is honoured only when it is a non-default one
 * (2/3) and the slot is the picked provider's on the picked date: a default
 * "1" (or null) means "no rule fired", never "book in 1", so a Frisco desk
 * booking a home-1 provider still asks for 2. Provider rows that do carry
 * home_departmentid / config (a future providers answer) feed rules 1-2
 * directly.
 *
 * Values the practice does not list are ignored at every step.
 */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

// provider_master.config arrives as an object or as a JSON string.
function configObject(config) {
  if (!config) return {};
  if (typeof config === "object") return config;
  try {
    const parsed = JSON.parse(config);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (e) {
    return {};
  }
}

const isoDay = (value) => {
  const day = String(value || "").slice(0, 10);
  return ISO_DATE.test(day) ? day : "";
};

const present = (value) => value !== undefined && value !== null && value !== "";

// The server's department for this slot, when it is a non-default one and the
// slot is the picked provider's on the picked date; else null.
function slotDepartment(slot, providerId, visitDate) {
  if (!slot || typeof slot !== "object") return null;
  if (present(slot.providerid) && present(providerId) && String(slot.providerid) !== String(providerId)) return null;
  const slotDay = isoDay(slot.startdate);
  const day = isoDay(visitDate);
  if (slotDay && day && slotDay !== day) return null;
  return nonDefaultDepartmentId(slot.departmentid);
}

/*
 * The provider's department for the visit date by the API's rule (department
 * slot -> department_from -> home 2/3), or null when no rule fires (a home-1
 * or unknown provider, or a date before their move).
 */
export function providerDepartmentId({ slot, provider, providerId, visitDate } = {}) {
  const fromSlot = slotDepartment(slot, present(providerId) ? providerId : provider?.providerid, visitDate);
  if (fromSlot) return fromSlot;

  const config = configObject(provider?.config);
  const move = config.department_from;
  const day = isoDay(visitDate);
  const moveDay = move && typeof move === "object" ? isoDay(move.date) : "";
  // ISO dates (YYYY-MM-DD) compare correctly as strings.
  if (day && moveDay && day >= moveDay) {
    const moved = offeredDepartmentId(move.departmentid);
    if (moved) return moved;
  }

  return nonDefaultDepartmentId(provider?.home_departmentid);
}

export function bookingDepartmentId({ slot, provider, providerId, visitDate, requested } = {}) {
  const department = providerDepartmentId({ slot, provider, providerId, visitDate });
  if (department) return department;
  // No rule fired, so the provider is not at a provider-only location (CODE)
  // on this date: such a request books in the default department.
  if (isProviderOnlyDepartment(requested)) return defaultDepartmentId();
  return requested;
}

// The open-slots row a booking picked: the slot list's entry for `starttime`.
export function pickedSlot(slots, starttime) {
  return (Array.isArray(slots) ? slots : []).find((s) => s?.starttime === starttime);
}

// The providers-list entry for a provider id.
export function pickedProvider(providers, providerId) {
  return (Array.isArray(providers) ? providers : []).find((p) => String(p?.providerid) === String(providerId));
}

// The slot whose department the form shows before a time is picked: the picked
// one, else the first slot of the list (every slot of one provider and date
// carries the same server department).
export function departmentSlot(slots, starttime) {
  const list = Array.isArray(slots) ? slots : [];
  return pickedSlot(list, starttime) || list[0];
}

/*
 * What a booking sends for the department field of the booking form (owner
 * 10-28: the form shows the provider's department and staff may change it).
 *   picked = the department staff chose in the form, or null/"" when untouched;
 *   rule   = what bookingDepartmentId gives (the pre-selected value).
 * Untouched, or changed back to the rule's value: { departmentid: rule } only,
 * exactly the request care always sent (the API applies its rule). A
 * different listed department: { departmentid: picked, department_override:
 * true }, which the API books as asked (scope.booking_departmentid explicit).
 */
export function bookingDepartmentFields({ picked, rule } = {}) {
  const chosen = offeredDepartmentId(picked);
  if (chosen && String(chosen) !== String(rule ?? "")) {
    return { departmentid: chosen, department_override: true };
  }
  return { departmentid: rule };
}

/*
 * The orders Schedule popup's reschedule (review 10-28). The visit being moved
 * keeps its own provider there (the request sends none), so the department
 * default comes from the ROW: its provider + stored department through the
 * same rule (a slot list is used only when it is that provider's). Staff who
 * explicitly picked a department in the form get it with department_override
 * (even when it equals the default); otherwise nothing is added and the API
 * decides from the stored stamp, exactly as before.
 * Returns { defaultDepartment, fields }.
 */
export function rescheduleDepartmentFields({ row, picked, slots, providers, providerId, visitDate } = {}) {
  const rowProvider = row?.providerid;
  const sameProvider = present(rowProvider) && present(providerId) && String(rowProvider) === String(providerId);
  const defaultDepartment = bookingDepartmentId({
    slot: sameProvider ? departmentSlot(slots) : undefined,
    provider: pickedProvider(providers, rowProvider),
    providerId: rowProvider,
    visitDate,
    requested: row?.departmentid,
  });
  const chosen = offeredDepartmentId(picked);
  return {
    defaultDepartment,
    fields: chosen ? { departmentid: chosen, department_override: true } : {},
  };
}
